/**
 * /leave-policies — admin editor for per-org leave-policy overrides
 * (ADR 0008 D2, PR 4a).
 *
 * Visible to owner / admin / hr. The page lists one section per
 * country the org has active employees in. Within each country: a
 * row per leave type with the country default beside an editable
 * override input. Overrides below the country statutory floor are
 * rejected — client-side for fast feedback AND server-side as the
 * authoritative gate.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LeavePolicyRow,
  LeaveType,
  LeaveTypeDefinitionRow,
  CountryCode,
} from '@kora/types';
import { authedLayoutRoute } from './authed.js';
import {
  listLeavePolicies,
  updateLeavePolicy,
  updateLeaveSettings,
  createCustomLeaveType,
  updateCustomLeaveType,
  deleteCustomLeaveType,
} from '../api/leavePolicies.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

const ACCRUAL_OPTIONS: ReadonlyArray<{ value: 'lump_annual' | 'monthly'; label: string }> = [
  { value: 'lump_annual', label: 'Lump — granted at the start of the year' },
  { value: 'monthly', label: 'Monthly — accrues through the year' },
];
const CARRYOVER_OPTIONS: ReadonlyArray<{ value: 'forfeit' | 'cap' | 'all' | 'expiry'; label: string }> = [
  { value: 'forfeit', label: 'Forfeit unused at year-end' },
  { value: 'cap', label: 'Carry over up to a cap' },
  { value: 'all', label: 'Carry over all unused' },
  { value: 'expiry', label: 'Carry over, then expire' },
];

export const leavePoliciesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/leave-policies',
  component: LeavePoliciesPage,
});

function LeavePoliciesPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['leave-policies'],
    queryFn: listLeavePolicies,
  });

  if (query.isLoading) {
    return (
      <section className="p-8 max-w-4xl">
        <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
          Loading…
        </div>
      </section>
    );
  }
  if (query.isError || !query.data) {
    return (
      <section className="p-8 max-w-4xl">
        <Alert variant="error">Could not load leave policies. Try refreshing.</Alert>
      </section>
    );
  }

  const grouped = groupByCountry(query.data.policies);
  const countries = Object.keys(grouped) as CountryCode[];

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Leave policies</h1>
        <p className="text-sm text-gray-400">
          Your org&apos;s leave rules. Country defaults are shown for
          reference. Overrides apply on top — and can only grant
          MORE leave than the country floor, never less.
        </p>
        <p className="text-xs text-gray-500 leading-relaxed">
          Leave any field <span className="text-gray-400">empty</span> to use the country default.
          {' '}<span className="text-gray-400">Carryover</span> is max days carried into the next
          year (0 = unused leave forfeits at year-end).
          {' '}<span className="text-gray-400">Notice</span> is the minimum calendar days&apos;
          advance an employee must give (n/a for medical/emergency types).
        </p>
      </header>

      <HolidayCountingToggle value={query.data.settings.countPublicHolidaysInLeave} />

      <CustomLeaveTypesSection definitions={query.data.definitions} />

      {countries.length === 0 ? (
        <div className="rounded border border-gray-900 p-8 text-center text-gray-500 text-sm">
          No active employees on file yet. Add an employee from
          <Link to="/employees" className="underline ml-1 hover:text-white">
            Employees
          </Link>
          {' '}to see country-specific policy options here.
        </div>
      ) : (
        <div className="space-y-6">
          {countries.map((country) => (
            <CountrySection
              key={country}
              country={country}
              policies={grouped[country] ?? []}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function groupByCountry(
  rows: LeavePolicyRow[],
): Partial<Record<CountryCode, LeavePolicyRow[]>> {
  const out: Partial<Record<CountryCode, LeavePolicyRow[]>> = {};
  for (const r of rows) {
    const bucket = out[r.countryCode] ?? [];
    bucket.push(r);
    out[r.countryCode] = bucket;
  }
  return out;
}

function CountrySection({
  country,
  policies,
}: {
  country: CountryCode;
  policies: LeavePolicyRow[];
}): JSX.Element {
  return (
    <section className="rounded border border-gray-900 divide-y divide-gray-900">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400">
        {country} — {policies.length} leave type{policies.length === 1 ? '' : 's'}
      </header>
      <ul>
        {policies.map((row) => (
          <PolicyRow
            key={`${row.countryCode}.${row.leaveType}`}
            row={row}
          />
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Per-row editor
// ---------------------------------------------------------------------------

const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  annual: 'Annual leave',
  sick: 'Sick leave',
  maternity: 'Maternity leave',
  paternity: 'Paternity leave',
  compassionate: 'Compassionate leave',
  unpaid: 'Unpaid leave',
  casual: 'Casual leave',
  study: 'Study leave',
  other: 'Other',
};

/**
 * Org-wide public-holiday counting toggle (ADR-0022 C3). Off by default: a
 * public holiday inside a leave span does not burn a leave day. Flipping it
 * affects new requests only — already-submitted leave keeps its stamped value.
 */
function HolidayCountingToggle({ value }: { value: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (next: boolean) => updateLeaveSettings({ countPublicHolidaysInLeave: next }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['leave-policies'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not save the setting.');
    },
  });

  return (
    <div className="rounded border border-gray-900 p-4 flex items-start justify-between gap-4">
      <div className="space-y-1">
        <p className="text-sm text-white">Count public holidays as leave days</p>
        <p className="text-xs text-gray-500 max-w-xl">
          When off (default), a public holiday that falls inside a leave request
          is not counted against the employee&apos;s balance. Turn it on if your
          org counts holidays within leave. Weekends are always excluded. Changes
          apply to new requests only.
        </p>
        {error ? <Alert variant="error" compact>{error}</Alert> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={mutation.isPending}
        onClick={() => mutation.mutate(!value)}
        className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
          value ? 'bg-emerald' : 'bg-gray-700'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            value ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

// ── Custom leave types (ADR-0023 Phase 2) ───────────────────────────────────
// Org-defined leave types. Defined here but not yet usable end-to-end — Phase 3
// wires them into accrual, balances, and the request form.
function CustomLeaveTypesSection({ definitions }: { definitions: LeaveTypeDefinitionRow[] }): JSX.Element {
  const [showAdd, setShowAdd] = useState(false);
  return (
    <div className="rounded border border-gray-900 divide-y divide-gray-900">
      <div className="p-4 flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-sm text-white">Custom leave types</p>
          <p className="text-xs text-gray-500 max-w-xl">
            Leave types your org defines (e.g. Sabbatical, Exam). These are
            non-statutory — no country floor applies. Not yet available on the
            request form; they become usable in a later update.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd((s) => !s)}
          className="shrink-0 rounded border border-gray-700 px-3 py-1.5 text-xs text-white hover:border-gray-500"
        >
          {showAdd ? 'Cancel' : 'Add type'}
        </button>
      </div>

      {showAdd ? <AddCustomTypeForm onDone={() => setShowAdd(false)} /> : null}

      {definitions.length === 0 ? (
        <p className="p-4 text-xs text-gray-500">No custom leave types yet.</p>
      ) : (
        definitions.map((d) => <CustomTypeRow key={d.id} def={d} />)
      )}
    </div>
  );
}

function AddCustomTypeForm({ onDone }: { onDone: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [accrualStrategy, setAccrualStrategy] = useState<'lump_annual' | 'monthly'>('lump_annual');
  const [carryoverStrategy, setCarryoverStrategy] = useState<'forfeit' | 'cap' | 'all' | 'expiry'>('forfeit');
  const [annualDays, setAnnualDays] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createCustomLeaveType({
        key,
        label,
        accrualStrategy,
        carryoverStrategy,
        annualDays: annualDays.trim() === '' ? null : Number(annualDays),
      }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['leave-policies'] });
      onDone();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Could not create the type.',
      );
    },
  });

  return (
    <form
      className="p-4 space-y-3 bg-gray-950/40"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-xs text-gray-400">Key (permanent, lowercase)</span>
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="sabbatical"
            className="w-full rounded border border-gray-800 bg-black px-2 py-1.5 text-sm text-white font-mono"
          />
          <span className="text-[11px] text-gray-600">Can&apos;t be changed later, and can&apos;t be a built-in name (annual, sick…).</span>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-400">Label (display name)</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Sabbatical"
            className="w-full rounded border border-gray-800 bg-black px-2 py-1.5 text-sm text-white"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-400">Accrual</span>
          <select
            value={accrualStrategy}
            onChange={(e) => setAccrualStrategy(e.target.value as 'lump_annual' | 'monthly')}
            className="w-full rounded border border-gray-800 bg-black px-2 py-1.5 text-sm text-white"
          >
            {ACCRUAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-400">Carryover</span>
          <select
            value={carryoverStrategy}
            onChange={(e) => setCarryoverStrategy(e.target.value as 'forfeit' | 'cap' | 'all' | 'expiry')}
            className="w-full rounded border border-gray-800 bg-black px-2 py-1.5 text-sm text-white"
          >
            {CARRYOVER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-gray-400">Annual days (blank = uncapped)</span>
          <input
            value={annualDays}
            onChange={(e) => setAnnualDays(e.target.value)}
            inputMode="decimal"
            placeholder="e.g. 10"
            className="w-full rounded border border-gray-800 bg-black px-2 py-1.5 text-sm text-white"
          />
        </label>
      </div>
      {error ? <Alert variant="error" compact>{error}</Alert> : null}
      <button
        type="submit"
        disabled={mutation.isPending || key.trim() === '' || label.trim() === ''}
        className="rounded bg-emerald px-3 py-1.5 text-xs text-black font-medium disabled:opacity-50"
      >
        {mutation.isPending ? 'Creating…' : 'Create type'}
      </button>
    </form>
  );
}

function CustomTypeRow({ def }: { def: LeaveTypeDefinitionRow }): JSX.Element {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(def.label);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leave-policies'] });

  const save = useMutation({
    mutationFn: (body: { label?: string; active?: boolean }) => updateCustomLeaveType(def.id, body),
    onSuccess: async () => { setError(null); await invalidate(); },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save.'),
  });
  const del = useMutation({
    mutationFn: () => deleteCustomLeaveType(def.id),
    onSuccess: async () => { setError(null); await invalidate(); },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Could not delete.'),
  });

  const dirty = label.trim() !== def.label && label.trim() !== '';

  return (
    <div className={`p-4 space-y-2 ${def.active ? '' : 'opacity-60'}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-xs text-gray-400 rounded bg-gray-900 px-2 py-0.5">{def.key}</span>
        {!def.active ? <span className="text-[11px] text-amber">Retired</span> : null}
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="flex-1 min-w-[8rem] rounded border border-gray-800 bg-black px-2 py-1 text-sm text-white"
        />
        {dirty ? (
          <button
            type="button"
            onClick={() => save.mutate({ label: label.trim() })}
            disabled={save.isPending}
            className="rounded border border-gray-700 px-2 py-1 text-xs text-white hover:border-gray-500 disabled:opacity-50"
          >
            Save name
          </button>
        ) : null}
      </div>
      <div className="flex items-center gap-4 text-[11px] text-gray-500 flex-wrap">
        <span>{def.annualDays === null ? 'Uncapped' : `${def.annualDays} days/yr`}</span>
        <span>{def.accrualStrategy === 'lump_annual' ? 'Lump' : 'Monthly'}</span>
        <span>Carryover: {def.carryoverStrategy}</span>
        <div className="flex-1" />
        {/* Retire / reactivate */}
        <button
          type="button"
          onClick={() => save.mutate({ active: !def.active })}
          disabled={save.isPending}
          className="rounded border border-gray-700 px-2 py-1 text-white hover:border-gray-500 disabled:opacity-50"
        >
          {def.active ? 'Retire' : 'Reactivate'}
        </button>
        {/* Delete — only when there's no history; otherwise retire is the path */}
        <button
          type="button"
          onClick={() => del.mutate()}
          disabled={del.isPending || def.hasHistory}
          title={def.hasHistory ? 'Has leave history — retire it instead of deleting' : 'Delete permanently'}
          className="rounded border border-red-900 px-2 py-1 text-red-300 hover:border-red-700 disabled:opacity-40"
        >
          Delete
        </button>
      </div>
      {error ? <Alert variant="error" compact>{error}</Alert> : null}
    </div>
  );
}

// Statutory medical/emergency types can't require advance notice (ADR-0022
// C2) — the server rejects notice > 0 for them, so we don't render the input.
const NOTICE_EXEMPT_TYPES = new Set<LeaveType>(['sick', 'maternity', 'compassionate']);

function PolicyRow({ row }: { row: LeavePolicyRow }): JSX.Element {
  const queryClient = useQueryClient();
  const [annualInput, setAnnualInput] = useState<string>(
    formatOverrideForInput(row.override?.annualDaysOverride ?? null),
  );
  const [carryoverInput, setCarryoverInput] = useState<string>(
    formatOverrideForInput(row.override?.maxCarryoverDaysOverride ?? null),
  );
  const [noticeInput, setNoticeInput] = useState<string>(
    formatOverrideForInput(row.override?.minNoticeDaysOverride ?? null),
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const parsedAnnual = parseOverrideInput(annualInput);
      const parsedCarryover = parseOverrideInput(carryoverInput);
      const parsedNotice = parseOverrideInput(noticeInput);
      // Client-side floor checks — mirror the server gate. Server is
      // authoritative; these are fast-feedback only. The annual floor applies
      // only to STATUTORY types (ADR-0022 C1) — non-statutory types (casual,
      // study) accept any value ≥ 0, including 0 to disable.
      if (
        row.countryDefault.hasStatutoryFloor &&
        parsedAnnual !== null &&
        row.countryDefault.annualDays !== null &&
        parsedAnnual < row.countryDefault.annualDays
      ) {
        throw new Error(
          `Annual override (${parsedAnnual}) is below the ${row.countryCode} floor (${row.countryDefault.annualDays}).`,
        );
      }
      if (
        parsedCarryover !== null &&
        row.countryDefault.maxCarryoverDays !== null &&
        parsedCarryover < row.countryDefault.maxCarryoverDays
      ) {
        throw new Error(
          `Carryover override (${parsedCarryover}) is below the ${row.countryCode} floor (${row.countryDefault.maxCarryoverDays}).`,
        );
      }
      return updateLeavePolicy({
        countryCode: row.countryCode,
        leaveType: row.leaveType,
        body: {
          annualDaysOverride: parsedAnnual,
          maxCarryoverDaysOverride: parsedCarryover,
          // Statutory medical/emergency types can't carry a notice period
          // (the server 400s on > 0); their input isn't rendered, so this
          // stays null for them.
          minNoticeDaysOverride: parsedNotice,
        },
      });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['leave-policies'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string; error?: string } | null;
        setError(body?.message ?? 'Could not save the override.');
        return;
      }
      setError(err instanceof Error ? err.message : 'Save failed.');
    },
  });

  // Disabled types render as a no-edit info row — pointless to let
  // an HR user "override" a leave type the country spec doesn't even
  // recognize yet (e.g. paternity in PR 4a).
  if (!row.countryDefault.enabled) {
    return (
      <li className="px-4 py-3 text-sm flex items-center justify-between gap-4">
        <span className="text-gray-500">
          {LEAVE_TYPE_LABEL[row.leaveType]} — not currently offered for
          this country
        </span>
        <span className="text-xs text-gray-700">Disabled</span>
      </li>
    );
  }

  const annualInputId = `override-${row.countryCode}-${row.leaveType}`;
  const carryoverInputId = `carryover-${row.countryCode}-${row.leaveType}`;
  const noticeInputId = `notice-${row.countryCode}-${row.leaveType}`;
  const carryoverDefault = row.countryDefault.maxCarryoverDays;

  const inputClass =
    'w-full rounded border border-gray-700 bg-gray-900 px-2 py-1 text-white text-sm focus:border-white focus:outline-none';
  const labelClass = 'text-[10px] uppercase tracking-wide text-gray-500';

  return (
    <li className="px-4 py-4 grid gap-x-5 gap-y-3 items-start grid-cols-2 md:grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_5.5rem_auto]">
      {/* Type + country default — full width on mobile, first column on desktop. */}
      <div className="min-w-0 col-span-2 md:col-span-1">
        <p className="text-sm text-white">{LEAVE_TYPE_LABEL[row.leaveType]}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Default{' '}
          <span className="font-mono">{row.countryDefault.annualDays ?? '—'}</span> d/yr
          {carryoverDefault !== null ? (
            <>
              {' '}· carryover <span className="font-mono">{carryoverDefault}</span>
            </>
          ) : null}
        </p>
        {error ? <Alert variant="error" compact className="mt-1">{error}</Alert> : null}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={annualInputId} className={labelClass}>Days/year</label>
        <input
          id={annualInputId}
          type="number"
          min={0}
          max={366}
          step={0.5}
          value={annualInput}
          onChange={(e) => setAnnualInput(e.target.value)}
          placeholder="—"
          className={inputClass}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={carryoverInputId} className={labelClass}>Carryover</label>
        <input
          id={carryoverInputId}
          type="number"
          min={0}
          max={366}
          step={0.5}
          value={carryoverInput}
          onChange={(e) => setCarryoverInput(e.target.value)}
          placeholder={carryoverDefault !== null ? String(carryoverDefault) : '—'}
          className={inputClass}
        />
      </div>

      {/* Notice — the column is reserved for medical/emergency types (shown as
          n/a) so every row's inputs stay vertically aligned. */}
      <div className="flex flex-col gap-1">
        <label htmlFor={noticeInputId} className={labelClass}>Notice</label>
        {NOTICE_EXEMPT_TYPES.has(row.leaveType) ? (
          <span
            className="text-xs text-gray-600 px-2 py-1"
            title="A notice period can't be required for medical/emergency leave"
          >
            n/a
          </span>
        ) : (
          <input
            id={noticeInputId}
            type="number"
            min={0}
            max={365}
            step={1}
            value={noticeInput}
            onChange={(e) => setNoticeInput(e.target.value)}
            placeholder="0"
            className={inputClass}
          />
        )}
      </div>

      <div className="col-span-2 md:col-span-1 flex md:justify-end md:pt-[18px]">
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="text-xs rounded border border-gray-700 px-3 py-1.5 hover:bg-gray-900 disabled:opacity-50 whitespace-nowrap"
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </li>
  );
}

function formatOverrideForInput(v: number | null): string {
  return v === null ? '' : String(v);
}

function parseOverrideInput(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}
